// src/components/ModalOrdenCobroRemitos.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  InputGroup,
} from "react-bootstrap";

import moment from "moment";

import {
  getRemitosPendientesCliente,
  getRemitoById,
} from "../helpers/rutaRemitos";
import {
  postOrdenCobroRemito,
  editarOrdenCobroRemito,
  getOrdenCobroRemitoById,
} from "../helpers/rutaOrdenCobroRemitos";
import { getSaldosTesoreria } from "../helpers/rutaTesoreria";
import { generarPDFPendientesCliente } from "../helpers/pdfPendientesCliente";
import { getAjustesCtaCtePendientes } from "../helpers/rutaAjustesCtaCte";

const fmtFechaDoc = (d) => (d ? moment.utc(d).format("DD/MM/YYYY") : "");

const $ = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const moneyInput = (value) => {
  if (value === "" || value == null) return "";

  return Number(value || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseMoneyInput = (value) => {
  const raw = String(value ?? "")
    .replace(/[^\d.,-]/g, "")
    .trim();

  if (!raw) return 0;

  // Formato argentino: 100.000,50
  if (raw.includes(",")) {
    return Number(raw.replace(/\./g, "").replace(",", "."));
  }

  // Si escribe 100.000 lo tomamos como cien mil
  const dotCount = (raw.match(/\./g) || []).length;

  if (dotCount > 0) {
    const parts = raw.split(".");
    const last = parts[parts.length - 1];

    // 100.000 / 1.000.000
    if (dotCount > 1 || last.length === 3) {
      return Number(raw.replace(/\./g, ""));
    }

    // 100000.50
    return Number(raw);
  }

  return Number(raw);
};

const limpiarTextoRetencion = (value) => {
  let texto = String(value ?? "")
    .replace(/[^\d.,-]/g, "")
    .trim();

  if (!texto) return "";

  const negativo = texto.startsWith("-");
  texto = texto.replace(/-/g, "");

  const lastComma = texto.lastIndexOf(",");
  const lastDot = texto.lastIndexOf(".");
  const sepIndex = Math.max(lastComma, lastDot);

  let entero = sepIndex >= 0 ? texto.slice(0, sepIndex) : texto;
  let decimal = sepIndex >= 0 ? texto.slice(sepIndex) : "";

  // Quita ceros adelante: 01250 -> 1250, pero conserva 0,50
  entero = entero.replace(/^0+(?=\d)/, "");

  if (!entero && decimal) entero = "0";

  return `${negativo ? "-" : ""}${entero}${decimal}`;
};

const parseRetencionInput = (value) => {
  const raw = limpiarTextoRetencion(value);
  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  const sepIndex = Math.max(lastComma, lastDot);

  if (sepIndex >= 0) {
    const entero = raw.slice(0, sepIndex).replace(/[.,]/g, "");
    const decimal = raw.slice(sepIndex + 1).replace(/[^\d]/g, "");

    return Number(`${entero || 0}.${decimal || 0}`);
  }

  return Number(raw.replace(/[^\d-]/g, ""));
};

const IVA_105 = 1.105;
const LOGO_SAN_PANCRACIO = "/SanPancracio.png";

const renderCuentaBancoLabel = (c) =>
  `${c.banco || c.descripcion || c.alias} — Saldo: $ ${$(c.saldoActual || 0)}`;

const toISOorNull = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(yyyy_mm_dd + "T00:00:00");
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const cleanOrEmpty = (s) => String(s || "").trim();
const cleanOrNull = (s) => {
  const t = String(s || "").trim();
  return t ? t : null;
};

// Calcula Venta A / Venta B (blanco/negro) del lado VENTA
function calcularTotalesVenta(rem) {
  let totalA = Number(rem.totalVentaA || 0); // blanco + IVA
  let totalB = Number(rem.totalVentaB || 0); // negro

  const kg = Number(rem.kgRecibido || 0);
  const pv = Number(rem.precioVenta || 0);

  const base =
    rem.totalVenta != null
      ? Number(rem.totalVenta || 0)
      : kg * pv;

  // Si no vienen A y B, recalculo con porcentajeVenta
  if (totalA <= 0.01 && totalB <= 0.01 && base > 0) {
    const porcB =
      Number(rem.porcentajeVenta || 0) / 100;

    const ventaNegro = base * porcB;
    const ventaBlancoBase = base - ventaNegro;
    const ventaBlancoIVA =
      ventaBlancoBase * IVA_105;

    totalA = ventaBlancoIVA;
    totalB = ventaNegro;
  }

  // El flete puede ser:
  // - TARIFA_KG: valor por kg
  // - IMPORTE_TOTAL: importe final histórico
  const valorFlete = Number(rem.flete || 0);
  const modoFlete = String(rem.modoFlete || "TARIFA_KG");

  let flete = 0;

  if (rem.factura !== false) {
    flete =
      modoFlete === "IMPORTE_TOTAL"
        ? Number(valorFlete.toFixed(2))
        : Number((kg * valorFlete).toFixed(2));
  }

  const tarifaFlete =
    modoFlete === "IMPORTE_TOTAL"
      ? 0
      : valorFlete;

  return {
    totalA,
    totalB,
    flete,
    tarifaFlete,
    modoFlete,
};
}

// Aplica cada importe únicamente a su cuenta.
function distribuirCobroPorCuenta(
  documentos,
  montos,
  esAjuste = false
) {
  const restantes = { ...montos };
  const apps = [];

  const orden = [...documentos].sort(
    (a, b) =>
      new Date(a.fecha || 0) -
        new Date(b.fecha || 0) ||
      Number(a.codrem || 0) -
        Number(b.codrem || 0) ||
      String(a._id || "").localeCompare(
        String(b._id || "")
      )
  );

  for (const doc of orden) {
    const totales = esAjuste
      ? null
      : calcularTotalesVenta(doc);

    const pendientes = esAjuste
      ? {
          A: doc.saldoA,
          B: doc.saldoB,
          Flete: doc.saldoFlete,
        }
      : {
          A:
            doc.pendienteA ??
            (
              totales.totalA -
              Number(
                doc.cobradoVentaA || 0
              )
            ),

          B:
            doc.pendienteB ??
            (
              totales.totalB -
              Number(
                doc.cobradoVentaB || 0
              )
            ),

          Flete:
            doc.pendienteFlete ??
            (
              totales.flete -
              Number(
                doc.cobradoFlete || 0
              )
            ),
        };

    const ap = {
      [esAjuste ? "ajuste" : "remito"]:
        doc._id,
      aplicado: 0,
    };

    for (const k of ["A", "B", "Flete"]) {
      const importe = Number(
        Math.min(
          Math.max(
            0,
            Number(
              pendientes[k] || 0
            )
          ),
          restantes[k]
        ).toFixed(2)
      );

      ap[`aplicado${k}`] = importe;

      ap.aplicado = Number(
        (
          ap.aplicado + importe
        ).toFixed(2)
      );

      restantes[k] = Number(
        (
          restantes[k] - importe
        ).toFixed(2)
      );
    }

    if (ap.aplicado > 0) {
      apps.push(ap);
    }
  }

  return {
    apps,
    restantes,
    restante: Number(
      (
        restantes.A +
        restantes.B +
        restantes.Flete
      ).toFixed(2)
    ),
  };
}

export default function ModalOrdenCobroRemitos({
  show,
  onHide,
  cliente,
  proveedor = null,
  preseleccion = [],
  ocId = null,
}) {
  const hoy = useMemo(() => moment().format("YYYY-MM-DD"), []);

  const esEdicion = Boolean(ocId);

  const [ocOriginal, setOcOriginal] = useState(null);

  const clienteActivo =
    esEdicion ? ocOriginal?.cliente : cliente;

  const proveedorActivo =
    esEdicion ? ocOriginal?.proveedor : proveedor;

  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [obs, setObs] = useState("");

  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]);

  const [ajustesPendientes, setAjustesPendientes] = useState([]);
  const [seleccionAjustes, setSeleccionAjustes] = useState([]);

  // Filtro opcional por proveedor
  const [filtrarProveedor, setFiltrarProveedor] = useState(false);

  // Qué conceptos de VENTA se cobran
  const [cobraA, setCobraA] = useState(false);
  const [cobraB, setCobraB] = useState(false);
  const [cobraFlete, setCobraFlete] = useState(false);

  const [
    importesCuenta,
    setImportesCuenta,
  ] = useState({
    A: "",
    B: "",
    Flete: "",
  });

  // cobros / retenciones
  const [medios, setMedios] = useState([]);
  const [rets, setRets] = useState([]);

  const [importeEditando, setImporteEditando] = useState(null);
  const [importeTexto, setImporteTexto] = useState({});

  const [retEditando, setRetEditando] = useState(null);
  const [retTexto, setRetTexto] = useState({});

  // combos tesorería
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  // estilos parejos (labels iguales)
  const labelStyle = useMemo(
    () => ({ fontSize: 12, fontWeight: 700, marginBottom: 2 }),
    []
  );
  const helpStyle = useMemo(() => ({ fontSize: 11, color: "#6c757d" }), []);
  const cardStyle = useMemo(
    () => ({
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 12,
      background: "#f8fafc",
    }),
    []
  );

  const gridStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: 10,
      alignItems: "end",
    }),
    []
  );

  const updMedio = useCallback((idx, patch) => {
    setMedios((p) => p.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  }, []);

  const toggleOpen = useCallback((idx) => {
    setMedios((p) =>
      p.map((m, i) => (i === idx ? { ...m, _open: !(m._open !== false) } : m))
    );
  }, []);

  const makeCheque = useCallback(
    () => ({
      medio: "cheque",
      importe: 0,
      _open: true,

      // comunes
      fechaCheque: hoy,
      fechaLiquidacion: "",
      beneficiario: "",
      cuitBeneficiario: "",
      observaciones: "",
      referencia: "",

      // cheque
      nroCheque: "",
      bancoEmisor: "",
      librador: "",
      cuitLibrador: "",
      fechaPagoDiferido: "", // opcional legacy
    }),
    [hoy]
  );

  const makeICheq = useCallback(
    () => ({
      medio: "icheq",
      importe: 0,
      _open: true,

      // comunes
      fechaCheque: hoy, // fecha iCheq
      fechaLiquidacion: "",
      beneficiario: "",
      cuitBeneficiario: "",
      observaciones: "",
      referencia: "",

      // icheq
      idICheq: "",
      emisor: "",
      cuitEmisor: "",
    }),
    [hoy]
  );

  // ------------- totales pendientes cliente -------------
  const totalesPendientes = pendientes.reduce(
    (acc, r) => {
      const pendA = Number(
        r.pendienteA ??
          Math.max(
            0,
            Number(r.totalVentaA || 0) -
              Number(r.cobradoVentaA || 0)
          )
      );

      const pendB = Number(
        r.pendienteB ??
          Math.max(
            0,
            Number(r.totalVentaB || 0) -
              Number(r.cobradoVentaB || 0)
          )
      );

      const pendF = Number(
        r.pendienteFlete ??
          Math.max(
            0,
            Number(r.flete || 0) -
              Number(r.cobradoFlete || 0)
          )
      );

      const saldo = Number(
        (pendA + pendB + pendF).toFixed(2)
      );

      acc.ventaA += pendA;
      acc.ventaB += pendB;
      acc.flete += pendF;
      acc.saldo += saldo;

      return acc;
    },
    {
      ventaA: 0,
      ventaB: 0,
      flete: 0,
      saldo: 0,
    }
  );

  const handlePdfPendientes = () => {
    generarPDFPendientesCliente({
      cliente: clienteActivo,
      proveedor: filtrarProveedor ? proveedorActivo : null,
      pendientes,
      totales: totalesPendientes,
      fecha,
      logoUrl: LOGO_SAN_PANCRACIO,
      empresa: {
        nombre: "SAN PANCRACIO SRL",
        direccion:
          "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
        telefono: "Tel: 0381-3008829",
        email: "contacto@sanpancracio.com",
      },
    }).catch((err) => {
      console.error("Error generando PDF de pendientes (cliente):", err);
      alert("No se pudo generar el PDF de pendientes.");
    });
  };

  // Σ selección
  const totalSeleccion = useMemo(
    () =>
      seleccion.reduce(
        (a, r) => a + Number(r.saldo ?? r.saldoPendiente ?? 0),
        0
      ),
    [seleccion]
  );

  const totalSeleccionAjustes = useMemo(
    () =>
      seleccionAjustes.reduce(
        (a, x) => a + Number(x.saldoTotal ?? x.saldo ?? 0),
        0
      ),
    [seleccionAjustes]
  );

  const totalSeleccionGeneral = useMemo(
    () => Number((Number(totalSeleccion) + Number(totalSeleccionAjustes)).toFixed(2)),
    [totalSeleccion, totalSeleccionAjustes]
  );
  
  const totalCobros = useMemo(
    () => medios.reduce((a, m) => a + Number(m.importe || 0), 0),
    [medios]
  );

  const totalRets = useMemo(
    () => rets.reduce((a, r) => a + Number(r.importe || 0), 0),
    [rets]
  );

  const montoAplicable = useMemo(
    () => Number((Number(totalCobros) + Number(totalRets)).toFixed(2)),
    [totalCobros, totalRets]
  );

  const hayConceptosCobro =
  cobraA || cobraB || cobraFlete;

  // Distribución automática aplicable en Venta A / B

  const cantidadCuentas =
    Number(cobraA) +
    Number(cobraB) +
    Number(cobraFlete);

  const montosPorCuenta = useMemo(() => {
    const seleccionadas = {
      A: cobraA,
      B: cobraB,
      Flete: cobraFlete,
    };

    return Object.fromEntries(
      Object.entries(seleccionadas).map(
        ([k, activa]) => [
          k,
          !activa
            ? 0
            : cantidadCuentas === 1
            ? montoAplicable
            : parseMoneyInput(
                importesCuenta[k]
              ),
        ]
      )
    );
  }, [
    cobraA,
    cobraB,
    cobraFlete,
    cantidadCuentas,
    montoAplicable,
    importesCuenta,
  ]);

  const sumaCuentas = Number(
    (
      montosPorCuenta.A +
      montosPorCuenta.B +
      montosPorCuenta.Flete
    ).toFixed(2)
  );

  const repartoValido =
    hayConceptosCobro &&
    Object.values(montosPorCuenta).every(
      (n) =>
        Number.isFinite(n) &&
        n >= 0
    ) &&
    Math.round(sumaCuentas * 100) ===
      Math.round(montoAplicable * 100);

  const aplicacionesDistribuidas =
    useMemo(
      () =>
        distribuirCobroPorCuenta(
          seleccion,
          repartoValido
            ? montosPorCuenta
            : {
                A: 0,
                B: 0,
                Flete: 0,
              }
        ),
      [
        seleccion,
        repartoValido,
        montosPorCuenta,
      ]
    );

  const aplicacionesAjustesDistribuidas =
    useMemo(
      () =>
        distribuirCobroPorCuenta(
          seleccionAjustes,
          aplicacionesDistribuidas.restantes,
          true
        ),
      [
        seleccionAjustes,
        aplicacionesDistribuidas,
      ]
    );

  const saldoFavorGenerado =
    aplicacionesAjustesDistribuidas.restante;
    
  const diferenciaVisual = useMemo(
    () => (totalSeleccionGeneral - montoAplicable).toFixed(2),
    [totalSeleccionGeneral, montoAplicable]
  );

  // Warnings mínimos
  const cobroWarnings = useMemo(() => {
    const w = [];
    medios.forEach((m, i) => {
      const n = i + 1;

      if (m.medio === "efectivo" && !m.caja)
        w.push(`Cobro #${n}: seleccione una Caja.`);
      if (
        (m.medio === "transferencia" ||
          m.medio === "deposito") &&
        !m.cuentaBancaria
      ) {
        w.push(`Cobro #${n}: seleccione una Cuenta bancaria.`);
      }

      if (m.medio === "cheque") {
        if (!cleanOrEmpty(m.nroCheque)) w.push(`Cobro #${n}: falta Nro Cheque.`);
        if (!m.fechaCheque) w.push(`Cobro #${n}: falta Fecha Cheque.`);
      }
      if (m.medio === "icheq") {
        if (!cleanOrEmpty(m.idICheq)) w.push(`Cobro #${n}: falta ID iCheq.`);
        if (!m.fechaCheque) w.push(`Cobro #${n}: falta Fecha iCheq.`);
      }
    });
    return w;
  }, [medios]);

  const canSubmit =
    repartoValido &&
    totalCobros > 0 &&
    montoAplicable > 0 &&
    Boolean(
      clienteActivo?._id ||
        clienteActivo
    );
  // ----------- cargar OC cuando estamos editando -----------
  useEffect(() => {
    if (!show) return;

    if (!esEdicion) {
      setOcOriginal(null);
      return;
    }

    (async () => {
      try {
        const r = await getOrdenCobroRemitoById(ocId);

        if (!r?.ok || !r?.orden) {
          alert(r?.msg || "No se pudo cargar la Orden de Cobro.");
          return;
        }

        const oc = r.orden;

        if (
          String(oc.estado || "").toLowerCase() === "anulada"
        ) {
          alert("No se puede editar una Orden anulada.");
          onHide(false);
          return;
        }

        setOcOriginal(oc);

        setFecha(
          oc.fecha
            ? moment(oc.fecha).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD")
        );

        setObs(oc.observaciones || "");

        setFiltrarProveedor(Boolean(oc.proveedor));

        const apps = oc.aplicaciones || [];
        const appsAj = oc.aplicacionesAjustes || [];

        const importesOriginales = {};

        for (const k of ["A", "B", "Flete"]) {
          importesOriginales[k] = Number(
            (
              [...apps, ...appsAj].reduce(
                (s, ap) =>
                  s +
                  Number(
                    ap[`aplicado${k}`] || 0
                  ),
                0
              ) +
              Number(
                oc.saldoFavor?.[
                  `generado${k}`
                ] || 0
              )
            ).toFixed(2)
          );
        }

        setCobraA(
          importesOriginales.A > 0
        );

        setCobraB(
          importesOriginales.B > 0
        );

        setCobraFlete(
          importesOriginales.Flete > 0
        );

        setImportesCuenta(
          Object.fromEntries(
            Object.entries(
              importesOriginales
            ).map(([k, n]) => [
              k,
              moneyInput(n),
            ])
          )
        );

        setMedios(
          (oc.cobros || oc.pagos || []).map((m) => ({
            ...m,

            caja:
              m?.caja?._id ||
              m?.caja ||
              "",

            cuentaBancaria:
              m?.cuentaBancaria?._id ||
              m?.cuentaBancaria ||
              "",

            fechaCheque: m.fechaCheque
              ? moment.utc(m.fechaCheque).format("YYYY-MM-DD")
              : "",

            fechaLiquidacion: m.fechaLiquidacion
              ? moment
                  .utc(m.fechaLiquidacion)
                  .format("YYYY-MM-DD")
              : "",

            fechaPagoDiferido: m.fechaPagoDiferido
              ? moment
                  .utc(m.fechaPagoDiferido)
                  .format("YYYY-MM-DD")
              : "",

            _open: false,
          }))
        );

        setRets(
          (oc.retenciones || []).map((r) => ({
            tipo: r.tipo || "IIBB",
            base: Number(r.base || 0),
            porcentaje: Number(r.porcentaje || 0),
            importe: Number(r.importe || 0),
            comprobante: r.comprobante || "",
          }))
        );
      } catch (e) {
        console.error("Error cargando OC para editar:", e);

        alert(
          e?.response?.data?.msg ||
            e.message ||
            "Error cargando la Orden de Cobro."
        );
      }
    })();
  }, [show, esEdicion, ocId, onHide]);

  // ----------- cargar data (pendientes + tesorería) -----------
  useEffect(() => {
    const clienteId =
      clienteActivo?._id || clienteActivo;

    const proveedorId =
      proveedorActivo?._id || proveedorActivo;

    if (!show || !clienteId) return;

    (async () => {
      const basePendientes =
        await getRemitosPendientesCliente(
          clienteId,
          filtrarProveedor
            ? proveedorId
            : undefined
        );

      // En edición puede haber remitos de la OC que ya quedaron
      // totalmente cobrados y por eso no aparecen como pendientes.
      // Los volvemos a incorporar.
      const mapBase = new Map();

      (basePendientes || []).forEach((r) => {
        mapBase.set(String(r._id), r);
      });

      if (esEdicion && ocOriginal) {
        (ocOriginal.aplicaciones || []).forEach((ap) => {
          const rem = ap?.remito;

          const id =
            rem?._id ||
            rem;

          if (!id) return;

          if (!mapBase.has(String(id))) {
            mapBase.set(
              String(id),
              typeof rem === "object"
                ? rem
                : { _id: id }
            );
          }
        });
      }

      const baseList = [...mapBase.values()];
      const list = await Promise.all(
        (baseList || []).map(async (r) => {
          try {
            const resp = await getRemitoById(r._id);
            const rem = resp?.remito || resp || {};
            const appOriginal =
              esEdicion && ocOriginal
                ? (ocOriginal.aplicaciones || []).find((ap) => {
                    const id =
                      ap?.remito?._id ||
                      ap?.remito;

                    return String(id) === String(r._id);
                  })
                : null;
            const {
              totalA,
              totalB,
              flete,
              tarifaFlete,
              modoFlete,
            } = calcularTotalesVenta(rem);

            const cobradoCliente =
              Number(rem.cobradoCliente || 0);

            const cobradoVentaA =
              Number(rem.cobradoVentaA || 0);

            const cobradoVentaB =
              Number(rem.cobradoVentaB || 0);

            const cobradoFlete =
              Number(rem.cobradoFlete || 0);

            const pendienteA = Math.max(
              0,
              totalA -
                cobradoVentaA +
                Number(appOriginal?.aplicadoA || 0)
            );

            const pendienteB = Math.max(
              0,
              totalB -
                cobradoVentaB +
                Number(appOriginal?.aplicadoB || 0)
            );

            const pendienteFlete = Math.max(
              0,
              flete -
                cobradoFlete +
                Number(appOriginal?.aplicadoFlete || 0)
            );
            
            const saldoPendiente = Number(
              (
                pendienteA +
                pendienteB +
                pendienteFlete
              ).toFixed(2)
            );

            return {
              ...r,

              totalVentaA: totalA,
              totalVentaB: totalB,

              // Flete real a cobrar
              modoFlete,
              tarifaFlete,
              flete,

              cobradoCliente,
              cobradoVentaA,
              cobradoVentaB,
              cobradoFlete,

              pendienteA,
              pendienteB,
              pendienteFlete,

              saldoPendiente,
            };
          } catch (err) {
            console.error("Error cargando detalle remito", r._id, err);
            return {
              ...r,
              totalVentaA: 0,
              totalVentaB: 0,
              cobradoCliente: 0,
              cobradoVentaA: 0,
              cobradoVentaB: 0,
              pendienteA: 0,
              pendienteB: 0,
              saldoPendiente: 0,
              tarifaFlete: 0,
              flete: 0,
              cobradoFlete: 0,
              pendienteFlete: 0,
            };
          }
        })
      );

      const ordenadas = (list || [])
        .slice()
        .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

      const map = new Map(
        ordenadas.map((r) => [String(r._id), r])
      );

      const sel = [];

      if (esEdicion && ocOriginal) {
        const idsOriginales = new Set(
          (ocOriginal.aplicaciones || []).map((ap) =>
            String(
              ap?.remito?._id ||
              ap?.remito
            )
          )
        );

        ordenadas.forEach((r) => {
          if (!idsOriginales.has(String(r._id))) return;

          const saldo = Number(
            (
              Number(r.pendienteA || 0) +
              Number(r.pendienteB || 0) +
              Number(r.pendienteFlete || 0)
            ).toFixed(2)
          );

          if (saldo > 0.01) {
            sel.push({
              ...r,
              saldo,
            });
          }
        });
      } else {
        const seed = [
          ...preseleccion,
          ...seleccion.filter((x) =>
            map.has(String(x._id))
          ),
        ];

        seed.forEach((s) => {
          const r = map.get(String(s._id));

          if (!r) return;

          const saldo = Number(
            (
              Number(r.pendienteA || 0) +
              Number(r.pendienteB || 0) +
              Number(r.pendienteFlete || 0)
            ).toFixed(2)
          );

          if (saldo > 0.01) {
            sel.push({
              ...r,
              saldo,
            });
          }
        });
      }

      setPendientes(ordenadas);
      setSeleccion(sel);

      try {
        const ajustes = await getAjustesCtaCtePendientes({
          entidadTipo: "CLIENTE",
          cliente: clienteId,
          movimiento: "A_COBRAR",
        });

      const mapAjustes = new Map();

      (ajustes || []).forEach((a) => {
        mapAjustes.set(String(a._id), a);
      });

      // En edición reincorporamos los ajustes usados por esta misma OC,
      // aunque actualmente hayan quedado en saldo cero.
      if (esEdicion && ocOriginal) {
        (ocOriginal.aplicacionesAjustes || []).forEach((ap) => {
          const aj = ap?.ajuste;

          const id =
            aj?._id ||
            aj;

          if (!id) return;

          if (!mapAjustes.has(String(id))) {
            mapAjustes.set(
              String(id),
              typeof aj === "object"
                ? aj
                : { _id: id }
            );
          }
        });
      }

      const ajustesOrdenados = [...mapAjustes.values()]
        .map((a) => {
          const appOriginal =
            esEdicion && ocOriginal
              ? (ocOriginal.aplicacionesAjustes || []).find((ap) => {
                  const id =
                    ap?.ajuste?._id ||
                    ap?.ajuste;

                  return String(id) === String(a._id);
                })
              : null;

          const saldoA = Number(a.saldoA || 0) +
            Number(appOriginal?.aplicadoA || 0);

          const saldoB = Number(a.saldoB || 0) +
            Number(appOriginal?.aplicadoB || 0);

          const saldoFlete = Number(a.saldoFlete || 0) +
            Number(appOriginal?.aplicadoFlete || 0);

          const saldoTotal = Number(
            (
              saldoA +
              saldoB +
              saldoFlete
            ).toFixed(2)
          );

          return {
            ...a,
            saldoA,
            saldoB,
            saldoFlete,
            saldoTotal,
            saldo: saldoTotal,
          };
        })
        .filter((a) => a.saldoTotal > 0.01)
        .sort(
          (a, b) =>
            new Date(a.fecha || 0) -
            new Date(b.fecha || 0)
        );

      setAjustesPendientes(ajustesOrdenados);

      if (esEdicion && ocOriginal) {
        const idsOriginales = new Set(
          (ocOriginal.aplicacionesAjustes || []).map((ap) =>
            String(
              ap?.ajuste?._id ||
              ap?.ajuste
            )
          )
        );

        setSeleccionAjustes(
          ajustesOrdenados.filter((a) =>
            idsOriginales.has(String(a._id))
          )
        );
      } else {
        setSeleccionAjustes((prev) =>
          prev.filter((x) =>
            ajustesOrdenados.some(
              (a) => String(a._id) === String(x._id)
            )
          )
        );
      }
      } catch (err) {
        console.error("Error cargando ajustes pendientes cliente", err);
        setAjustesPendientes([]);
        setSeleccionAjustes([]);
}

      try {
        const s = await getSaldosTesoreria();
        if (s?.ok) {
          setCajas(Array.isArray(s.cajas) ? s.cajas : []);
          setCuentas(Array.isArray(s.cuentas) ? s.cuentas : []);
        }
      } catch (err) {
        console.error("Error cargando saldos tesorería", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    clienteActivo?._id,
    proveedorActivo?._id,
    filtrarProveedor,
    esEdicion,
    ocOriginal,
  ]);

  // ----------- selección -----------
  const toggle = (r) => {
    setSeleccion((prev) => {
      const ex = prev.find(
        (x) => x._id === r._id
      );

      if (ex) {
        return prev.filter(
          (x) => x._id !== r._id
        );
      }

      const pendA = Number(
        r.pendienteA ?? 0
      );

      const pendB = Number(
        r.pendienteB ?? 0
      );

      const pendF = Number(
        r.pendienteFlete ?? 0
      );

      const saldo = Number(
        (pendA + pendB + pendF).toFixed(2)
      );

      return saldo > 0.01
        ? [...prev, { ...r, saldo }]
        : prev;
    });
  };

 const tildarTodas = () =>
  setSeleccion(
    pendientes
      .map((r) => {
        const pendA = Number(
          r.pendienteA ?? 0
        );

        const pendB = Number(
          r.pendienteB ?? 0
        );

        const pendF = Number(
          r.pendienteFlete ?? 0
        );

        const saldo = Number(
          (pendA + pendB + pendF).toFixed(2)
        );

        return { ...r, saldo };
      })
      .filter((r) => r.saldo > 0.01)
  );

  const destildarTodas = () => setSeleccion([]);

  const toggleAjuste = (a) => {
    setSeleccionAjustes((prev) => {
      const ex = prev.find((x) => x._id === a._id);
      if (ex) return prev.filter((x) => x._id !== a._id);

      const saldoTotal = Number(a.saldoTotal ?? a.saldo ?? 0);
      return saldoTotal > 0.01 ? [...prev, { ...a, saldo: saldoTotal }] : prev;
    });
  };

  const tildarTodosAjustes = () =>
    setSeleccionAjustes(
      ajustesPendientes
        .map((a) => ({
          ...a,
          saldo: Number(a.saldoTotal ?? a.saldo ?? 0),
        }))
        .filter((a) => a.saldo > 0.01)
    );

  const destildarTodosAjustes = () => setSeleccionAjustes([]);

  // ----------- cobros / retenciones -----------
  const addMedio = (m) => setMedios((p) => [...p, m]);
  const rmMedio = (i) => setMedios((p) => p.filter((_, idx) => idx !== i));
  const addRet = (r) => setRets((p) => [...p, r]);
  const rmRet = (i) => setRets((p) => p.filter((_, idx) => idx !== i));

  // ----------- guardar -----------
  const guardar = async () => {
    try {
      if (!canSubmit) return;

      if (!hayConceptosCobro) {
        alert("Marcá al menos un concepto a cobrar: Venta A, Venta B o Flete.");
        return;
      }

      if (cobroWarnings.length > 0) {
        const seguir = window.confirm(
          "Hay advertencias:\n- " +
            cobroWarnings.join("\n- ") +
            "\n\n¿Deseás continuar de todas formas?"
        );
        if (!seguir) return;
      }

      const clienteId =
        clienteActivo?._id || clienteActivo;

      const proveedorId =
        proveedorActivo?._id || proveedorActivo;

      const payload = {
        cliente: clienteId,

        proveedor: filtrarProveedor
          ? proveedorId
          : esEdicion
          ? null
          : undefined,

        fecha,
        observaciones: obs,

        // NUEVO: le decimos al backend que aplique por antigüedad
        modoAplicacion: seleccion.length > 0 ? "antiguedad" : "manual",

        montosPorCuenta,

        conceptos: {
          cobraA,
          cobraB,
          cobraFlete,
        },

        // Si no hay remitos seleccionados, no mandamos remitos para que el backend no aplique por antigüedad.
        remitosIds: seleccion.length > 0 ? seleccion.map((r) => r._id) : [],

        // Para modo manual sin remitos
        aplicaciones: seleccion.length > 0 ? undefined : [],

        aplicacionesAjustes: aplicacionesAjustesDistribuidas.apps,

        cobros: medios.map((m) => {
          const base = {
            medio: m.medio,
            importe: Number(m.importe || 0),
            referencia: cleanOrEmpty(m.referencia),

            // comunes cheque/icheq
            fechaCheque: toISOorNull(m.fechaCheque) || undefined,
            fechaLiquidacion: toISOorNull(m.fechaLiquidacion) || undefined,
            beneficiario: cleanOrNull(m.beneficiario),
            cuitBeneficiario: cleanOrNull(m.cuitBeneficiario),
            observaciones: cleanOrNull(m.observaciones),
          };

          if (m.medio === "efectivo") return { ...base, caja: m.caja };
          if (
            m.medio === "transferencia" ||
            m.medio === "deposito"
          ) {
            return {
              ...base,
              cuentaBancaria: m.cuentaBancaria,
            };
          }

          if (m.medio === "cheque") {
            return {
              ...base,
              nroCheque: cleanOrNull(m.nroCheque),
              bancoEmisor: cleanOrNull(m.bancoEmisor),
              librador: cleanOrNull(m.librador),
              cuitLibrador: cleanOrNull(m.cuitLibrador),
              fechaPagoDiferido: toISOorNull(m.fechaPagoDiferido) || undefined,
            };
          }

          if (m.medio === "icheq") {
            return {
              ...base,
              idICheq: cleanOrNull(m.idICheq),
              emisor: cleanOrNull(m.emisor),
              cuitEmisor: cleanOrNull(m.cuitEmisor),
            };
          }

          return base;
        }),

        retenciones: rets.map((r) => ({
          tipo: (r.tipo || "IIBB").toUpperCase(),
          base: Number(r.base || 0),
          porcentaje: Number(r.porcentaje || 0),
          importe: Number(r.importe || 0),
          comprobante: r.comprobante || "",
        })),
      };
      const res = esEdicion
        ? await editarOrdenCobroRemito(ocId, payload)
        : await postOrdenCobroRemito(payload);

      if (res?.ok) {
        const orden = res?.orden || {};

        const numeros = [];

        if (orden.numeroA) {
          numeros.push(`Cuenta A: ${orden.numeroA}`);
        }

        if (orden.numeroB) {
          numeros.push(`Cuenta B: ${orden.numeroB}`);
        }

        // Si es una OC solamente de Flete,
        // no tendrá numeración A/B.
        if (!orden.numeroA && !orden.numeroB && orden.numero) {
          numeros.push(`Orden: ${orden.numero}`);
        }

        const detalle =
          numeros.length > 0
            ? `\n\n${numeros.join("\n")}`
            : "";

        alert(
          esEdicion
            ? `Orden de cobro editada correctamente.${detalle}`
            : `Orden de cobro guardada correctamente.${detalle}`
        );

        onHide(true);
      } else {
        alert(res?.msg || "Error al guardar.");
      }
    } catch (e) {
      console.error("OC Remitos error:", e?.response?.data || e);
      alert(e?.response?.data?.msg || e.message || "Error al guardar.");
    }
  };

  const updRet = (idx, patch) => {
    setRets((p) => p.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };

  const retKey = (idx, campo) => `${idx}-${campo}`;

  const retMoneyValue = (idx, r, campo) => {
    const key = retKey(idx, campo);

    if (retEditando === key) {
      return retTexto[key] ?? "";
    }

    const valor = Number(r?.[campo] || 0);
    return valor ? moneyInput(valor) : "";
  };

  const retDecimalValue = (idx, r, campo) => {
    const key = retKey(idx, campo);

    if (retEditando === key) {
      return retTexto[key] ?? "";
    }

    const valor = Number(r?.[campo] || 0);
    return valor ? String(valor).replace(".", ",") : "";
  };

  const onFocusRet = (idx, campo, valor) => {
    const key = retKey(idx, campo);

    setRetEditando(key);
    setRetTexto((prev) => ({
      ...prev,
      [key]: valor ? String(valor).replace(".", ",") : "",
    }));
  };

  const onBlurRet = (idx, campo) => {
    const key = retKey(idx, campo);

    setRetEditando(null);
    setRetTexto((prev) => {
      const nuevo = { ...prev };
      delete nuevo[key];
      return nuevo;
    });
  };

  const nombreCli =
    (
      clienteActivo?.razonsocial ||
      clienteActivo?.nombre ||
      ""
    ).toString();

  const nombreProv =
    (
      proveedorActivo?.razonsocial ||
      proveedorActivo?.nombre ||
      ""
    ).toString();

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {esEdicion
            ? `Editar Orden de Cobro ${
                [ocOriginal?.numeroA, ocOriginal?.numeroB]
                  .filter(Boolean)
                  .join(" / ") ||
                ocOriginal?.numero ||
                ""
              }`
            : "Nueva Orden de Cobro (Remitos)"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* headers */}
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Cliente</Form.Label>
            <Form.Control readOnly value={nombreCli} />
          </Col>
          <Col md={4}>
            <Form.Label>Proveedor (filtro opcional)</Form.Label>
            <Form.Control readOnly value={nombreProv} placeholder="(sin filtro)" />
            <div className="mt-1">
              <Form.Check
                type="switch"
                id="filtrar-por-proveedor"
                label="Filtrar por este proveedor"
                checked={filtrarProveedor}
                onChange={(e) => setFiltrarProveedor(e.target.checked)}
              />
            </div>
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Col>
        </Row>

        {/* pendientes */}
        <Row className="align-items-center mb-2">
          <Col>
            <b>Remitos pendientes del cliente</b>
          </Col>
          <Col className="text-end">
            <Button size="sm" variant="outline-secondary" onClick={tildarTodas}>
              Tildar todos
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={destildarTodas}
            >
              Destildar todos
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handlePdfPendientes}
              disabled={esEdicion}
            >
              PDF pendientes
            </Button>{" "}
            {Boolean(seleccion.length) && (
              <span className="ms-2">
                Seleccionados: <b>{seleccion.length}</b> — Σ:{" "}
                <b>${$(totalSeleccion)}</b>
              </span>
            )}
          </Col>
        </Row>

        <div
          style={{
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f8f9fa",
                  zIndex: 2,
                }}
              >
                <th style={{ width: 40 }}></th>
                <th>Remito</th>
                <th>Fecha</th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Venta A (blanco + IVA)
                </th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Venta B (negro)
                </th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Flete
                </th>

                <th style={{ textAlign: "right" }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((r) => {
                const checked = !!seleccion.find((x) => x._id === r._id);

                const pendA = Number(
                  r.pendienteA ??
                    Math.max(
                      0,
                      Number(r.totalVentaA || 0) -
                        Number(r.cobradoVentaA || 0)
                    )
                );
                const pendB = Number(
                  r.pendienteB ??
                    Math.max(
                      0,
                      Number(r.totalVentaB || 0) -
                        Number(r.cobradoVentaB || 0)
                    )
                );
                const pendF = Number(
                  r.pendienteFlete ??
                    Math.max(
                      0,
                      Number(r.flete || 0) -
                        Number(r.cobradoFlete || 0)
                    )
                );

                const saldo = Number((pendA + pendB + pendF).toFixed(2));
                const disabled = saldo <= 0.01;
                const id = `sel-r-v-${r._id}`;

                return (
                  <tr
                    key={r._id}
                    style={disabled ? { opacity: 0.5 } : undefined}
                  >
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={id}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggle(r)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <label htmlFor={id} className="w-100 m-0">
                        #{String(r.codrem).padStart(6, "0")}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={id} className="w-100 m-0">
                        {fmtFechaDoc(r.fecha)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(pendA)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(pendB)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(pendF)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(saldo)}
                      </label>
                    </td>
                  </tr>
                );
              })}
              {!pendientes.length && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    Sin pendientes para este cliente/proveedor
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* resumen pendientes */}
        <div
          className="mt-2 p-2 rounded"
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #90caf9",
            color: "#0d47a1",
            fontWeight: "bold",
          }}
        >
          <Row>
            <Col md={3}>
              Σ Pendiente Venta A: $
              {$(totalesPendientes.ventaA)}
            </Col>

            <Col md={3}>
              Σ Pendiente Venta B: $
              {$(totalesPendientes.ventaB)}
            </Col>

            <Col md={3}>
              Σ Flete pendiente: $
              {$(totalesPendientes.flete)}
            </Col>

            <Col md={3}>
              Σ Saldo pendiente total: $
              {$(totalesPendientes.saldo)}
            </Col>
          </Row>
        </div>

        {/* ajustes pendientes */}
        <div className="mt-3">
          <Row className="align-items-center mb-2">
            <Col>
              <b>Ajustes pendientes del cliente</b>
              <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                Solo ajustes A_COBRAR pendientes/parciales
              </span>
            </Col>

            <Col className="text-end">
              <Button size="sm" variant="outline-secondary" onClick={tildarTodosAjustes}>
                Tildar ajustes
              </Button>{" "}
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={destildarTodosAjustes}
              >
                Destildar ajustes
              </Button>{" "}
              {Boolean(seleccionAjustes.length) && (
                <span className="ms-2">
                  Ajustes seleccionados: <b>{seleccionAjustes.length}</b> — Σ:{" "}
                  <b>${$(totalSeleccionAjustes)}</b>
                </span>
              )}
            </Col>
          </Row>

          <div
            style={{
              maxHeight: 180,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            <Table size="sm" bordered hover className="m-0">
              <thead>
                <tr
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#fff8e1",
                    zIndex: 2,
                  }}
                >
                  <th style={{ width: 40 }}></th>
                  <th>Número</th>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th style={{ textAlign: "right" }}>Saldo A</th>
                  <th style={{ textAlign: "right" }}>Saldo B</th>
                  <th style={{ textAlign: "right" }}>Flete</th>
                  <th style={{ textAlign: "right" }}>Saldo</th>
                </tr>
              </thead>

              <tbody>
                {ajustesPendientes.map((a) => {
                  const checked = !!seleccionAjustes.find((x) => x._id === a._id);
                  const saldoA = Number(a.saldoA || 0);
                  const saldoB = Number(a.saldoB || 0);
                  const saldoFlete = Number(a.saldoFlete || 0);
                  const saldoTotal = Number(a.saldoTotal ?? saldoA + saldoB + saldoFlete);
                  const disabled = saldoTotal <= 0.01;
                  const id = `sel-ajuste-${a._id}`;

                  return (
                    <tr key={a._id} style={disabled ? { opacity: 0.5 } : undefined}>
                      <td style={{ width: 40, textAlign: "center" }}>
                        <input
                          id={id}
                          type="checkbox"
                          className="form-check-input m-0 p-0"
                          disabled={disabled}
                          checked={checked}
                          onChange={() => toggleAjuste(a)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>

                      <td>
                        <label htmlFor={id} className="w-100 m-0">
                          {a.numero || "-"}
                        </label>
                      </td>

                      <td>
                        <label htmlFor={id} className="w-100 m-0">
                          {fmtFechaDoc(a.fecha)}
                        </label>
                      </td>

                      <td>
                        <label htmlFor={id} className="w-100 m-0">
                          {a.concepto || "-"}
                        </label>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <label htmlFor={id} className="w-100 m-0">
                          ${$(saldoA)}
                        </label>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <label htmlFor={id} className="w-100 m-0">
                          ${$(saldoB)}
                        </label>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <label htmlFor={id} className="w-100 m-0">
                          ${$(saldoFlete)}
                        </label>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <label htmlFor={id} className="w-100 m-0">
                          ${$(saldoTotal)}
                        </label>
                      </td>
                    </tr>
                  );
                })}

                {!ajustesPendientes.length && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted">
                      Sin ajustes pendientes para este cliente
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* conceptos */}
        <Row className="mt-2">
          <Col>
            <Form.Check
              inline
              type="checkbox"
              id="cobra-a"
              label="Cobrar Venta A (blanco)"
              checked={cobraA}
              onChange={(e) => setCobraA(e.target.checked)}
            />
            <Form.Check
              inline
              type="checkbox"
              id="cobra-b"
              label="Cobrar Venta B (negro)"
              checked={cobraB}
              onChange={(e) => setCobraB(e.target.checked)}
            />
            <Form.Check
              inline
              type="checkbox"
              id="cobra-flete"
              label="Cobrar Flete"
              checked={cobraFlete}
              onChange={(e) =>
                setCobraFlete(e.target.checked)
              }
            />
          </Col>
        </Row>

        <Row className="mt-2">
        <Col md={12}>
          <small>
            El importe de cada cuenta incluye
            las retenciones. El excedente queda
            a favor en esa misma cuenta.
          </small>
        </Col>

        {[
          ["A", cobraA],
          ["B", cobraB],
          ["Flete", cobraFlete],
        ]
          .filter(([, activa]) => activa)
          .map(([cuenta]) => (
            <Col
              md={4}
              key={cuenta}
            >
              <Form.Label>
                Importe destinado a {cuenta}
              </Form.Label>

              <Form.Control
                type="text"
                inputMode="decimal"
                readOnly={
                  cantidadCuentas === 1
                }
                value={
                  cantidadCuentas === 1
                    ? moneyInput(
                        montoAplicable
                      )
                    : importesCuenta[
                        cuenta
                      ]
                }
                placeholder="0,00"
                onChange={(e) =>
                  setImportesCuenta(
                    (prev) => ({
                      ...prev,
                      [cuenta]:
                        e.target.value,
                    })
                  )
                }
              />
            </Col>
          ))}

        {cantidadCuentas > 1 && (
          <Col
            md={12}
            className={
              repartoValido
                ? "text-success"
                : "text-danger"
            }
          >
            Asignado: ${$(sumaCuentas)}
            {" / "}
            Cobros + retenciones: $
            {$(montoAplicable)}.

            {!repartoValido &&
              " Indicá los importes por cuenta hasta completar el total."}
          </Col>
        )}
      </Row>

        {/* cobros / retenciones */}
        <Row className="mt-3">
          <Col md={6}>
            <h6>Cobros</h6>

            <Button
              size="sm"
              onClick={() =>
                addMedio({ medio: "transferencia", importe: 0, cuentaBancaria: "" })
              }
            >
              + Transferencia
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "deposito",
                  importe: 0,
                  cuentaBancaria: "",
                })
              }
            >
              + Depósito
            </Button>{" "}
            <Button size="sm" onClick={() => addMedio(makeCheque())}>
              + Cheque
            </Button>{" "}
            <Button size="sm" onClick={() => addMedio(makeICheq())}>
              + iCheq
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addMedio({ medio: "efectivo", importe: 0, caja: "" })}
            >
              + Efectivo
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Medio</th>
                  <th>Destino / Datos</th>
                  <th style={{ width: 180 }}>Importe</th>
                  <th style={{ width: 55 }}></th>
                </tr>
              </thead>

              <tbody>
                {medios.map((m, i) => {
                  const isCheque = m.medio === "cheque";
                  const isICheq = m.medio === "icheq";
                  const open = m._open !== false;

                  const selCaja = cajas.find((c) => String(c._id) === String(m.caja));
                  const selCta = cuentas.find((c) => String(c._id) === String(m.cuentaBancaria));

                  const resumenCheque = [
                    cleanOrEmpty(m.nroCheque) ? `Nro: ${m.nroCheque}` : "",
                    cleanOrEmpty(m.bancoEmisor) ? `Banco: ${m.bancoEmisor}` : "",
                    m.fechaCheque ? `F: ${moment(m.fechaCheque).format("DD/MM/YYYY")}` : "",
                  ].filter(Boolean).join(" — ");

                  const resumenICheq = [
                    cleanOrEmpty(m.idICheq) ? `ID: ${m.idICheq}` : "",
                    cleanOrEmpty(m.emisor) ? `Emisor: ${m.emisor}` : "",
                    m.fechaCheque ? `F: ${moment(m.fechaCheque).format("DD/MM/YYYY")}` : "",
                  ].filter(Boolean).join(" — ");

                  return (
                    <React.Fragment key={i}>
                      {/* FILA PRINCIPAL */}
                      <tr key={`main-${i}`}>
                        <td style={{ verticalAlign: "top" }}>
                          <Form.Select
                            size="sm"
                            value={m.medio}
                            onChange={(e) => {
                              const v = e.target.value;
                                if (
                                  v === "transferencia" ||
                                  v === "deposito"
                                ) {
                                  updMedio(i, {
                                    medio: v,
                                    importe: 0,
                                    cuentaBancaria: "",
                                    caja: "",
                                    referencia: "",
                                    _open: false,
                                  });
                                  return;
                                }
                              if (v === "efectivo") {
                                updMedio(i, {
                                  medio: v,
                                  importe: 0,
                                  caja: "",
                                  cuentaBancaria: "",
                                  referencia: "",
                                  _open: false,
                                });
                                return;
                              }
                              if (v === "cheque") {
                                updMedio(i, makeCheque());
                                return;
                              }
                              if (v === "icheq") {
                                updMedio(i, makeICheq());
                                return;
                              }
                            }}
                          >
                            <option value="transferencia">Transferencia</option>
                            <option value="deposito">Depósito</option>
                            <option value="cheque">Cheque</option>
                            <option value="icheq">iCheq</option>
                            <option value="efectivo">Efectivo</option>
                          </Form.Select>

                          {(isCheque || isICheq) && (
                            <button
                              type="button"
                              className="btn btn-link p-0 mt-1"
                              style={{ fontSize: 12, textDecoration: "none" }}
                              onClick={() => toggleOpen(i)}
                            >
                              {open ? "▲ Ocultar datos" : "▼ Mostrar datos"}
                            </button>
                          )}
                        </td>

                        {/* Destino / Datos (compacto) */}
                        <td>
                          {m.medio === "efectivo" && (
                            <div className="d-flex gap-2 align-items-center">
                              <Form.Select
                                size="sm"
                                value={m.caja || ""}
                                style={{ minWidth: 280 }}
                                onChange={(e) => updMedio(i, { caja: e.target.value })}
                              >
                                <option value="">-- Caja --</option>
                                {cajas.map((c) => (
                                  <option key={c._id} value={c._id}>
                                    {c.descripcion} — Saldo: ${$(c.saldoActual || 0)}
                                  </option>
                                ))}
                              </Form.Select>
                              {selCaja && (
                                <span className="text-muted">
                                  Saldo: ${$(selCaja.saldoActual || 0)}
                                </span>
                              )}
                            </div>
                          )}

                          {(m.medio === "transferencia" ||  m.medio === "deposito") && (
                            <div className="d-flex gap-2 align-items-center">
                              <Form.Select
                                size="sm"
                                value={m.cuentaBancaria || ""}
                                style={{ minWidth: 280 }}
                                onChange={(e) =>
                                  updMedio(i, { cuentaBancaria: e.target.value })
                                }
                              >
                                <option value="">-- Cuenta --</option>
                                {cuentas.map((c) => (
                                  <option key={c._id} value={c._id}>
                                    {renderCuentaBancoLabel(c)}
                                  </option>
                                ))}
                              </Form.Select>
                              {selCta && (
                                <span className="text-muted">
                                  Saldo: ${$(selCta.saldoActual || 0)}
                                </span>
                              )}
                            </div>
                          )}

                          {(isCheque || isICheq) && (
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              {isCheque ? (resumenCheque || "—") : (resumenICheq || "—")}
                            </div>
                          )}
                        </td>

                        {/* Importe */}
                        <td style={{ verticalAlign: "top" }}>
                          <Form.Label style={labelStyle}>Importe *</Form.Label>

                          <InputGroup size="sm">
                            <InputGroup.Text>$</InputGroup.Text>

                            <Form.Control
                              type="text"
                              inputMode="decimal"
                              className="text-end"
                              value={
                                importeEditando === i
                                  ? importeTexto[i] ?? String(m.importe || "")
                                  : moneyInput(m.importe)
                              }
                              onFocus={() => {
                                setImporteEditando(i);
                                setImporteTexto((prev) => ({
                                  ...prev,
                                  [i]: m.importe ? String(m.importe).replace(".", ",") : "",
                                }));
                              }}
                              onChange={(e) => {
                                const texto = e.target.value;

                                setImporteTexto((prev) => ({
                                  ...prev,
                                  [i]: texto,
                                }));

                                const importe = parseMoneyInput(texto);

                                updMedio(i, {
                                  importe: Number.isFinite(importe) ? importe : 0,
                                });
                              }}
                              onBlur={() => {
                                setImporteEditando(null);

                                setImporteTexto((prev) => {
                                  const nuevo = { ...prev };
                                  delete nuevo[i];
                                  return nuevo;
                                });
                              }}
                            />
                          </InputGroup>
                        </td>

                        <td style={{ verticalAlign: "top" }}>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => rmMedio(i)}
                          >
                            x
                          </Button>
                        </td>
                      </tr>
                      
                      {/* FILA DETALLE (colSpan) */}
                      {(isCheque || isICheq) && open && (
                        <tr key={`detail-${i}`}>
                          <td colSpan={4}>
                            <div style={cardStyle}>
                              <div style={gridStyle}>
                                {/* Identificador */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Nro Cheque *" : "ID iCheq *"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.nroCheque || "") : (m.idICheq || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { nroCheque: e.target.value }
                                        : { idICheq: e.target.value }
                                      )
                                    }
                                    placeholder={isCheque ? "Ej: 123456" : "Ej: 1121"}
                                  />
                                </div>

                                {/* Banco / Emisor */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Banco emisor" : "Emisor"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.bancoEmisor || "") : (m.emisor || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { bancoEmisor: e.target.value }
                                        : { emisor: e.target.value }
                                      )
                                    }
                                    placeholder="Ej: Santander"
                                  />
                                </div>

                                {/* Fecha Cheque / iCheq */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Fecha Cheque" : "Fecha iCheq"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={m.fechaCheque || ""}
                                    onChange={(e) =>
                                      updMedio(i, { fechaCheque: e.target.value })
                                    }
                                  />
                                </div>

                                {/* Fecha liquidación */}
                                <div>
                                  <Form.Label style={labelStyle}>Fecha liquidación</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={m.fechaLiquidacion || ""}
                                    onChange={(e) =>
                                      updMedio(i, { fechaLiquidacion: e.target.value })
                                    }
                                  />
                                  <div style={helpStyle}>Opcional (cuando se acredita)</div>
                                </div>

                                {/* CUIT emisor/librador */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "CUIT Librador" : "CUIT Emisor"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.cuitLibrador || "") : (m.cuitEmisor || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { cuitLibrador: e.target.value }
                                        : { cuitEmisor: e.target.value }
                                      )
                                    }
                                    placeholder="Sólo números"
                                  />
                                </div>

                                {/* Librador / Cobrado a */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Cobrado a (Librador)" : "Cobrado a (Emisor)"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.librador || "") : (m.emisor || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { librador: e.target.value }
                                        : { emisor: e.target.value }
                                      )
                                    }
                                    placeholder="Nombre / razón social"
                                  />
                                </div>

                                {/* Fecha pago diferido (solo cheque) */}
                                <div>
                                  {isCheque ? (
                                    <>
                                      <Form.Label style={labelStyle}>Fecha pago diferido</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        type="date"
                                        value={m.fechaPagoDiferido || ""}
                                        onChange={(e) =>
                                          updMedio(i, { fechaPagoDiferido: e.target.value })
                                        }
                                      />
                                      <div style={helpStyle}>Legacy / opcional</div>
                                    </>
                                  ) : (
                                    <div />
                                  )}
                                </div>

                                {/* Beneficiario */}
                                <div>
                                  <Form.Label style={labelStyle}>Pagado a (Beneficiario)</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.beneficiario || ""}
                                    onChange={(e) =>
                                      updMedio(i, { beneficiario: e.target.value })
                                    }
                                    placeholder="Ej: SAN PANCRACIO SRL"
                                  />
                                </div>

                                {/* CUIT Beneficiario */}
                                <div>
                                  <Form.Label style={labelStyle}>CUIT Beneficiario</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.cuitBeneficiario || ""}
                                    onChange={(e) =>
                                      updMedio(i, { cuitBeneficiario: e.target.value })
                                    }
                                    placeholder="Sólo números"
                                  />
                                </div>

                                {/* Referencia */}
                                <div>
                                  <Form.Label style={labelStyle}>Referencia (opcional)</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.referencia || ""}
                                    onChange={(e) =>
                                      updMedio(i, { referencia: e.target.value })
                                    }
                                    placeholder="Texto libre"
                                  />
                                </div>

                                {/* Observaciones */}
                                <div style={{ gridColumn: "1 / -1" }}>
                                  <Form.Label style={labelStyle}>Observaciones</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.observaciones || ""}
                                    onChange={(e) =>
                                      updMedio(i, { observaciones: e.target.value })
                                    }
                                    placeholder="Notas internas"
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>  
                  );
                })}

                {!medios.length && (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      Agregá al menos un cobro
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {cobroWarnings.length > 0 && (
              <ul className="mt-2" style={{ color: "crimson", fontSize: 13 }}>
                {cobroWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </Col>

          {/* retenciones */}
          <Col md={6}>
            <h6>Retenciones</h6>
            <Button
              size="sm"
              onClick={() => addRet({ tipo: "IIBB", base: "", porcentaje: "", importe: "" })}
            >
              + IIBB
            </Button>{" "}

            <Button
              size="sm"
              onClick={() => addRet({ tipo: "GAN", base: "", porcentaje: "", importe: "" })}
            >
              + Ganancias
            </Button>{" "}

            <Button
              size="sm"
              onClick={() => addRet({ tipo: "IVA", base: "", porcentaje: "", importe: "" })}
            >
              + IVA
            </Button>{" "}

            <Button
              size="sm"
              onClick={() => addRet({ tipo: "OTRA", base: "", porcentaje: "", importe: "" })}
            >
              + Otra
            </Button>       

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Base</th>
                  <th>%</th>
                  <th>Importe</th>
                  <th>Comp.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={r.tipo}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i ? { ...x, tipo: e.target.value } : x
                            )
                          )
                        }
                      >
                        <option value="IIBB">IIBB</option>
                        <option value="GAN">Ganancias</option>
                        <option value="IVA">IVA</option>
                        <option value="OTRA">OTRA</option>
                      </Form.Select>
                    </td>
                    <td style={{ width: 110 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        className="text-end"
                        placeholder="0,00"
                        value={retMoneyValue(i, r, "base")}
                        onFocus={() => onFocusRet(i, "base", r.base)}
                        onChange={(e) => {
                          const texto = limpiarTextoRetencion(e.target.value);

                          setRetTexto((prev) => ({
                            ...prev,
                            [retKey(i, "base")]: texto,
                          }));

                          const base = parseRetencionInput(texto);

                          updRet(i, {
                            base: Number.isFinite(base) ? base : 0,
                          });
                        }}
                        onBlur={() => onBlurRet(i, "base")}
                      />
                    </td>
                    <td style={{ width: 90 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        className="text-end"
                        placeholder="0,00"
                        value={retDecimalValue(i, r, "porcentaje")}
                        onFocus={() => onFocusRet(i, "porcentaje", r.porcentaje)}
                          onChange={(e) => {
                            const texto = limpiarTextoRetencion(e.target.value);

                            setRetTexto((prev) => ({
                              ...prev,
                              [retKey(i, "porcentaje")]: texto,
                            }));

                            const porcentaje = parseRetencionInput(texto);

                            updRet(i, {
                              porcentaje: Number.isFinite(porcentaje) ? porcentaje : 0,
                            });
                          }}
                        onBlur={() => onBlurRet(i, "porcentaje")}
                      />
                    </td>
                    <td style={{ width: 120 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        className="text-end"
                        placeholder="0,00"
                        value={retMoneyValue(i, r, "importe")}
                        onFocus={() => onFocusRet(i, "importe", r.importe)}
                          onChange={(e) => {
                            const texto = limpiarTextoRetencion(e.target.value);

                            setRetTexto((prev) => ({
                              ...prev,
                              [retKey(i, "importe")]: texto,
                            }));

                            const importe = parseRetencionInput(texto);

                            updRet(i, {
                              importe: Number.isFinite(importe) ? importe : 0,
                            });
                          }}
                        onBlur={() => onBlurRet(i, "importe")}
                      />
                    </td>
                    <td style={{ width: 140 }}>
                      <Form.Control
                        size="sm"
                        placeholder="Comprobante"
                        value={r.comprobante || ""}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? { ...x, comprobante: e.target.value }
                                : x
                            )
                          )
                        }
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => rmRet(i)}
                      >
                        x
                      </Button>
                    </td>
                  </tr>
                ))}
                {!rets.length && (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      Agregá retenciones si corresponde
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={3}>
            <b>Σ Selección:</b> ${$(totalSeleccionGeneral)}
          </Col>
          <Col md={3}>
            <b>Σ Cobros:</b> ${$(totalCobros)}
          </Col>
          <Col md={3}>
            <b>Σ Retenciones:</b> ${$(totalRets)}
          </Col>
          <Col md={3}>
            <b>Σ Aplicable:</b> ${$(montoAplicable)}
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <div className="text-muted">
              Diferencia (Selección − Aplicable):{" "}
              <b
                style={{
                  color:
                    Math.abs(Number(diferenciaVisual)) <= 0.01
                      ? "green"
                      : "crimson",
                }}
              >
                {$(diferenciaVisual)}
              </b>{" "}
              (informativa)
            </div>
            {saldoFavorGenerado > 0.01 && (
              <div className="text-warning mt-1">
                Saldo a favor total: $
                {$(saldoFavorGenerado)}.

                {["A", "B", "Flete"].map(
                  (cuenta) =>
                    aplicacionesAjustesDistribuidas
                      .restantes[cuenta] > 0 && (
                      <div key={cuenta}>
                        {cuenta}: $
                        {$(
                          aplicacionesAjustesDistribuidas
                            .restantes[cuenta]
                        )}{" "}
                        a favor.
                      </div>
                    )
                )}
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={guardar} disabled={!canSubmit}>
          {esEdicion ? "Guardar cambios" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
